import React from 'react';
import { Search } from '@mui/icons-material';
import { Button, FormControl, Grid, InputAdornment, TextField, TextFieldPropsColorOverrides, TextFieldPropsSizeOverrides } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';
import { OverridableStringUnion } from '@mui/types';

type SearchTextFieldProps = {
  /**
   * The search term in the text field.
   */
  term: string;

  /**
   * Called when the search term in the text field is updated.
   * See `delay` for details of debouncing.
   */
  onTermChange?: (term: string) => void;

  /**
   * `setTerm` will be called no more than once every `delay` milliseconds.
   * This can be used to reduce the number of network requests for new search terms.
   */
  delayMs?: number;

  /**
   * Activate the clear button functionality.
   */
  clear?: boolean;

  /**
   * Sets the justification for the search text box.
   */
  justify?: 'left' | 'center' | 'right' | 'flex-start' | 'flex-end' | 'start' | 'end';

  /**
   * Sets the width of the search text box to full width of the container if true.
   * Overrides 'justify'
   */
  fullWidth?: boolean;

  /**
   * Sets the search box title if present, if undefined the title is 'Search'.
   */
  title?: string;

  searchIcon?: boolean;

  displayError?: boolean;

  displayColor?: OverridableStringUnion<"error" | "primary" | "secondary" | "info" | "success" | "warning", TextFieldPropsColorOverrides>;

  size?: OverridableStringUnion<"small" | "medium", TextFieldPropsSizeOverrides>;

  width?: string;

  disabled?: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const testId = 'search-text-field-password';

/**
 * A full-width form containing a small text field for a search term
 * This provides normal editing of local state for the search term, then
 * at the specified interval updates the provided `term` data using `setTerm`.
 * This is intended to avoid calling server APIs too quickly while the user
 * is typing.
 */
const SearchTextField: React.FC<SearchTextFieldProps> = ({
  term,
  onTermChange,
  delayMs,
  justify,
  clear = false,
  fullWidth = false,
  title = 'Search',
  searchIcon = true,
  displayError = false,
  displayColor = undefined,
  size = 'small',
  width = undefined,
  disabled = false
}: SearchTextFieldProps) => {
  const [text, setText] = useState<string>(term);

  const debouncedText = useDebounce<string>(text, delayMs || 200);
  useEffect(() => onTermChange?.(debouncedText), [onTermChange, text, debouncedText]);

  const onTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setText(event.target.value), [setText]);

  const clearSearchTerm = () => {
    setText('');
  };

  return (
    <Grid container justifyContent={justify}>
      <Grid item flexGrow={fullWidth ? 1 : 0}>
        <FormControl size="small" fullWidth>
          <TextField
            data-testid={testId}
            size={size}
            value={text}
            sx={{ width: width }}
            onChange={onTextChange}
            label={title}
            error={displayError}
            color={displayColor}
            disabled={disabled}
            InputProps={{
              endAdornment: searchIcon && (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
      </Grid>
      {clear && <Grid item alignItems='stretch' style={{ display: 'flex' }}>
        <Button disableElevation variant='outlined' onClick={clearSearchTerm}>Clear</Button>
      </Grid>}
    </Grid>
  );
};

export default SearchTextField;
